export const EVENT_RECOVERY_OVERLAP_SECONDS = 60;

export interface RecoverableEventChange {
    eventId: string | null;
    lastUpdated: Date | string | null;
    created: Date | string | null;
    deleted: boolean | null;
}

export interface EventRevision {
    timestamp: number;
    deleted: boolean;
}

export interface EventRecoveryWatermarks {
    lastHttpDeltaCursorEpoch?: number;
    lastAppliedSignalREpoch?: number;
}

export interface EventChangeTransaction<T extends RecoverableEventChange> {
    id: number;
    changes: readonly T[];
}

export interface EventChangeTransactionSelection<T extends RecoverableEventChange> {
    acceptedChanges: T[];
    appliedEventIdsByTransaction: Map<number, string[]>;
}

const getEventTimestamp = (event: RecoverableEventChange): number => {
    const value = event.lastUpdated ?? event.created;
    const timestamp = value instanceof Date ? value.getTime() : Date.parse(value ?? '');
    return Number.isFinite(timestamp) ? timestamp : 0;
};

export const isNewerEventRevision = (candidate: EventRevision, current: EventRevision): boolean =>
    candidate.timestamp > current.timestamp || (candidate.timestamp === current.timestamp && candidate.deleted && !current.deleted);

/**
 * Removes replayed event IDs and refuses a stale HTTP replay after a newer
 * SignalR change has already been accepted. Equal revisions are duplicates;
 * a tombstone wins an equal-time live value so deletion cannot be resurrected.
 */
export const selectEventChangesForApplication = <T extends RecoverableEventChange>(
    changes: readonly T[],
    appliedRevisions: Map<string, EventRevision>
): T[] => {
    const newestInBatch = new Map<string, T>();

    changes.forEach((change) => {
        if (!change.eventId) {
            return;
        }

        const existing = newestInBatch.get(change.eventId);
        if (
            !existing ||
            isNewerEventRevision(
                { timestamp: getEventTimestamp(change), deleted: change.deleted === true },
                { timestamp: getEventTimestamp(existing), deleted: existing.deleted === true }
            )
        ) {
            newestInBatch.set(change.eventId, change);
        }
    });

    const accepted: T[] = [];
    newestInBatch.forEach((change, eventId) => {
        const candidate = { timestamp: getEventTimestamp(change), deleted: change.deleted === true };
        const applied = appliedRevisions.get(eventId);
        if (applied && !isNewerEventRevision(candidate, applied)) {
            return;
        }

        appliedRevisions.set(eventId, candidate);
        accepted.push(change);
    });
    return accepted;
};

/**
 * Selects one revision per event ID across all queued transactions. Consumers
 * must merge this result as one React commit, so acknowledgement belongs only
 * to the transaction that supplied the committed winning revision.
 */
export const selectEventChangeTransactionsForApplication = <T extends RecoverableEventChange>(
    transactions: readonly EventChangeTransaction<T>[],
    appliedRevisions: Map<string, EventRevision>
): EventChangeTransactionSelection<T> => {
    const newestInBatch = new Map<string, { change: T; transactionId: number }>();
    const appliedEventIdsByTransaction = new Map<number, string[]>();

    transactions.forEach((transaction) => {
        appliedEventIdsByTransaction.set(transaction.id, []);
        transaction.changes.forEach((change) => {
            if (!change.eventId) {
                return;
            }

            const existing = newestInBatch.get(change.eventId);
            if (
                !existing ||
                isNewerEventRevision(
                    { timestamp: getEventTimestamp(change), deleted: change.deleted === true },
                    { timestamp: getEventTimestamp(existing.change), deleted: existing.change.deleted === true }
                )
            ) {
                newestInBatch.set(change.eventId, { change, transactionId: transaction.id });
            }
        });
    });

    const acceptedChanges: T[] = [];
    newestInBatch.forEach(({ change, transactionId }, eventId) => {
        const candidate = { timestamp: getEventTimestamp(change), deleted: change.deleted === true };
        const applied = appliedRevisions.get(eventId);
        if (applied && !isNewerEventRevision(candidate, applied)) {
            return;
        }

        appliedRevisions.set(eventId, candidate);
        acceptedChanges.push(change);
        appliedEventIdsByTransaction.get(transactionId)?.push(eventId);
    });

    return { acceptedChanges, appliedEventIdsByTransaction };
};

export const getOverlappingRecoveryEpoch = (lastAppliedEpoch: number | undefined): number =>
    lastAppliedEpoch ? Math.max(0, lastAppliedEpoch - EVENT_RECOVERY_OVERLAP_SECONDS) : 0;

export const getHttpResponseEpoch = (lastUpdatedDateUtc: Date | string | null | undefined): number | undefined => {
    const timestamp = lastUpdatedDateUtc instanceof Date ? lastUpdatedDateUtc.getTime() : Date.parse(lastUpdatedDateUtc ?? '');
    if (!Number.isFinite(timestamp)) {
        return undefined;
    }

    // The existing delta endpoint accepts whole seconds. Do not introduce a
    // sub-second client cursor while its contract remains second-based.
    return Math.floor(timestamp / 1000);
};

export const getNextHttpDeltaCursorEpoch = (
    currentCursorEpoch: number | undefined,
    responseLastUpdatedDateUtc: Date | string | null | undefined
): number | undefined => {
    const responseEpoch = getHttpResponseEpoch(responseLastUpdatedDateUtc);
    if (responseEpoch === undefined) {
        return currentCursorEpoch;
    }

    return Math.max(currentCursorEpoch ?? 0, responseEpoch);
};

/**
 * Keeps the HTTP delta cursor and SignalR application watermark independent:
 * an endpoint response can update only its cursor, while a SignalR watermark
 * requires a successfully applied SignalR message epoch.
 */
export const getNextEventRecoveryWatermarks = (
    current: EventRecoveryWatermarks,
    responseLastUpdatedDateUtc?: Date | string | null,
    successfullyAppliedSignalREpoch?: number
): EventRecoveryWatermarks => ({
    lastHttpDeltaCursorEpoch: getNextHttpDeltaCursorEpoch(current.lastHttpDeltaCursorEpoch, responseLastUpdatedDateUtc),
    lastAppliedSignalREpoch:
        successfullyAppliedSignalREpoch && Number.isSafeInteger(successfullyAppliedSignalREpoch)
            ? Math.max(current.lastAppliedSignalREpoch ?? 0, successfullyAppliedSignalREpoch)
            : current.lastAppliedSignalREpoch,
});
